import React, { ErrorInfo, useEffect, useState } from 'react';
import Button, {
  ButtonSize,
  ButtonType
} from '../../shared/components/core-ui/button/button.component';
import { connect } from 'react-redux';
import styles from './usermanagement.module.scss';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core/styles';
import ReactDatePicker from 'react-datepicker';
import Select, { ValueType } from 'react-select';
import { Pagination } from '../../shared/components/core-ui/pagination/pagination.component';
import { InputText } from '../../shared/components/core-ui/input-text/input-text.component';
import { PUBLIC_PATH } from '../../shared/config/routes-config';
import { ReservationStatus } from '../../shared/enums/reservation-status';
import useWindowResizer from '../../shared/hooks/window-resizer/window-resizer.hook';
import { isErrorInfo } from '../../shared/interfaces/error-info.interface';
import { getAllAreas } from '../../shared/services/data/lookups.data';
import type { AreaUiModel } from '../../shared/ui-models/area.ui-model';
import type { ReservationUiModel } from '../../shared/ui-models/reservation.ui-model';
import { getModuleName } from '../../UserModule/shared/services/user.service';
import { getAllUsers } from './data.service';

const useRowStyles = makeStyles({
  root: {
    '& > *': {
      borderBottom: 'unset',
      fontSize: '1.5rem'
    }
  }
});

const useSelectedRowStyles = makeStyles({
  root: {
    '& > *': {
      borderBottom: 'unset',
      fontSize: '1.5rem'
    }
  }
});

export const UserManagementListContainer = () => {
  const isMobileView = useWindowResizer();

  const [primaryGuestPhone, setPrimaryGuestPhone] = useState('');
  const [primaryGuestName, setPrimaryGuestName] = useState('');
  const [area, setArea] = useState('');
  const [reservationStatuses, setReservationStatuses] = useState('');
  const [propertyIds, setPropertyIds] = useState('');
  const [areaOptions, setAreaOptions] = useState<{ value: string; label: string }[]>([]);
  const [checkInFromDate, setCheckInFromDate] = useState<Date>();
  const [checkInToDate, setCheckInToDate] = useState<Date>();
  const [checkOutFromDate, setCheckOutFromDate] = useState<Date>();
  const [checkOutToDate, setCheckOutToDate] = useState<Date>();

  const [users, setUsers] = useState<ReservationUiModel[]>([]);
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [listCount, setListCount] = useState<number>(0);
  const [offset, setOffset] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [searchQuery, setSearchQuery] = useState<any>({});

  useEffect(() => {
    // trackPage('reservations-list');
    getAllAreas(getModuleName()).then((response: AreaUiModel[] | ErrorInfo) => {
      if (!isErrorInfo(response)) {
        setAreaOptions(
          (response as AreaUiModel[]).map((area) => ({ value: area.id || '', label: area.name }))
        );
      }
    });
    getUsers({}, 1, limit);
  }, []);

  const getUsers = (query: object, offset: number, limit: number) => {
    setLoadingData(true);
    getAllUsers(query, offset, limit).then((response: any | ErrorInfo) => {
      setLoadingData(false);
      if (!isErrorInfo(response)) {
        setListCount(response?.count);
        setUsers(response?.data || []);
      } else {
        setUsers([]);
        setListCount(0);
      }
    });
  };

  const exportUsers = (query: object, offset: number, limit: number) => {
    setLoadingData(true);
    getAllUsers(query, offset, limit).then((response) => {
      setLoadingData(false);
      if (!isErrorInfo(response)) {
        downloadCSV(response);
      }
    });
  };

  function downloadCSV(file: any) {
    const link = document.createElement('a');

    var binaryData = [];
    binaryData.push(file);
    link.href = window.URL.createObjectURL(new Blob(binaryData, { type: 'application/csv' }));

    // link.href = URL.createObjectURL(file);
    link.setAttribute('download', 'reservation_data.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // }
  }

  const getNewUser = (offsetNumber: number) => {
    setOffset(offsetNumber);
    getUsers({ ...searchQuery }, offset, limit);
  };

  function Row(props: { user: any }) {
    const { user } = props;
    const classes = useRowStyles();
    const selectedClasses = useSelectedRowStyles();

    return (
      <>
        <TableRow
          className={
            user.status === ReservationStatus.CHECKED_IN_STARTED
              ? selectedClasses.root
              : classes.root
          }
        >
          <TableCell component="th" scope="row">
            {user.areaName}
          </TableCell>
          <TableCell>
            <div
              className={`${styles['user-management-container__user-item__status']} ${
                styles[`user-management-container__user-item__status--${user.status}`]
              }`}
            >
              {user.status ? user.status.replace('_', ' ') : ''}{' '}
            </div>
          </TableCell>
          <TableCell>{user.primaryGuestName}</TableCell>
          <TableCell>{user.primaryGuestPhone}</TableCell>
          <TableCell>{user.primaryGuestEmail}</TableCell>
          <TableCell>{new Date(user.checkInDate).toDateString()}</TableCell>
          <TableCell>{new Date(user.checkOutDate).toDateString()}</TableCell>
          <TableCell>{new Date(user.createdAt).toDateString()}</TableCell>
        </TableRow>
        <TableRow />
      </>
    );
  }

  const handleUserExportClick = () => {
    const query: any = {};

    if (primaryGuestPhone) {
      query.primaryGuestPhone = primaryGuestPhone;
    }
    if (primaryGuestName) {
      query.primaryGuestName = primaryGuestName;
    }
    if (area) {
      query.areaIds = [area];
    }

    if (reservationStatuses) {
      query.reservationStatuses = [reservationStatuses];
    }
    if (propertyIds) {
      query.propertyIds = [propertyIds];
    }

    if (checkInFromDate && checkInToDate) {
      query.checkInFrom = checkInFromDate.getTime();
      query.checkInTo = checkInToDate.getTime();
    }
    if (checkOutToDate && checkOutFromDate) {
      query.checkOutFrom = checkOutFromDate.getTime();
      query.checkOutTo = checkOutToDate.getTime();
    }
    query.export = true;
    setSearchQuery(query);
    setOffset(1);
    exportUsers({ ...query }, 1, limit);
  };
  const handleClearClick = () => {
    setOffset(1);
    setArea('');
    setPrimaryGuestPhone('');
    setPrimaryGuestName('');
    setPropertyIds('');
    setReservationStatuses('');
    setCheckInFromDate(undefined);
    setCheckInToDate(undefined);
    setCheckOutFromDate(undefined);
    setCheckOutToDate(undefined);
    setSearchQuery({});
    getUsers({}, 1, limit);
  };

  const handleUserSearchClick = () => {
    const query: any = {};

    if (primaryGuestPhone) {
      query.primaryGuestPhone = primaryGuestPhone;
    }
    if (primaryGuestName) {
      query.primaryGuestName = primaryGuestName;
    }
    if (area) {
      query.areaIds = [area];
    }

    if (reservationStatuses) {
      query.reservationStatuses = [reservationStatuses];
    }
    if (propertyIds) {
      query.propertyIds = [propertyIds];
    }

    if (checkInFromDate && checkInToDate) {
      query.checkInFrom = checkInFromDate.getTime();
      query.checkInTo = checkInToDate.getTime();
    }
    if (checkOutToDate && checkOutFromDate) {
      query.checkOutFrom = checkOutFromDate.getTime();
      query.checkOutTo = checkOutToDate.getTime();
    }
    setSearchQuery(query);
    setOffset(1);
    getUsers({ ...query }, 1, limit);
  };
  return (
    <div className={styles['user-management-container']}>
      <div className={styles['user-management-container__title']}>
        <span>User Management</span>
      </div>

      <div className={styles['user-management-container__user-item']}>
        <div className={styles['user-management-container__user-item__form-field']}>
          <div className={styles['user-management-container__user-item__form-field__label']}>
            PrimaryGuestName
          </div>
          <InputText
            data={{ value: primaryGuestName }}
            config={{ maxRows: 1, autoFocus: false }}
            eventHandlers={{
              onChangeHandler: (value: string) => setPrimaryGuestName(value)
            }}
          />
        </div>
        <div className={styles['user-management-container__user-item__form-field']}>
          <div className={styles['user-management-container__user-item__form-field__label']}>
            PrimaryGuestPhone
          </div>
          <InputText
            data={{
              value: primaryGuestPhone
            }}
            config={{
              maxRows: 1,
              autoFocus: false
            }}
            eventHandlers={{
              onChangeHandler: (value: string, isValid: boolean) => {
                setPrimaryGuestPhone(value);
              }
            }}
          />
        </div>
        <div className={styles['user-management-container__user-item__form-field']}>
          <div className={styles['user-management-container__user-item__form-field__label']}>
            PropertyId
          </div>
          <InputText
            data={{
              value: propertyIds
            }}
            config={{
              maxRows: 1,
              autoFocus: false
            }}
            eventHandlers={{
              onChangeHandler: (value: string, isValid: boolean) => {
                setPropertyIds(value);
              }
            }}
          />
        </div>
        <div className={styles['user-management-container__user-item__form-field']}>
          <div className={styles['user-management-container__user-item__form-field__label']}>
            Area
          </div>
          <Select
            value={{ value: area, label: areaOptions?.find((a) => a?.value === area)?.label }}
            options={areaOptions}
            className={styles['user-management-container__user-item__form-field__select']}
            onChange={(value: any) => {
              setArea(value ? value.value : '');
            }}
          />
        </div>

        <div className={styles['user-management-container__user-item__form-field']}>
          <div className={styles['user-management-container__user-item__form-field__label']}>
            reservationStatuses
          </div>
          <Select
            value={{ value: reservationStatuses, label: reservationStatuses?.replaceAll('_', ' ') }}
            options={Object.values(ReservationStatus)?.map((status) => ({
              value: status,
              label: status.charAt(0).toUpperCase() + status?.replaceAll('_', ' ')?.slice(1)
            }))}
            className={styles['user-management-container__user-item__form-field__select']}
            onChange={(value: ValueType<{ value: string; label: string }, false>) => {
              setReservationStatuses(value ? value.value : '');
            }}
          />
        </div>

        <div className={styles['user-management-container__user-item__form-field']}>
          <div className={styles['user-management-container__user-item__form-field__label']}>
            Check In / from-to
          </div>
          <div className={styles['user-management-container__user-item__date-range']}>
            <ReactDatePicker
              selected={checkInFromDate}
              onChange={(date: Date) => {
                setCheckInFromDate(date);
                setCheckInToDate(undefined);
              }}
              selectsStart
              startDate={checkInFromDate}
              endDate={checkInToDate}
              placeholderText="Start Date"
            />
            <ReactDatePicker
              selected={checkInToDate}
              onChange={(date: Date) => {
                setCheckInToDate(date);
              }}
              selectsEnd
              startDate={checkInFromDate}
              endDate={checkInToDate}
              minDate={checkInFromDate}
              placeholderText="End Date"
            />
          </div>
        </div>
        <div className={styles['user-management-container__user-item__form-field']}>
          <div className={styles['user-management-container__user-item__form-field__label']}>
            Check out / from-to
          </div>
          <div className={styles['user-management-container__user-item__date-range']}>
            <ReactDatePicker
              selected={checkOutFromDate}
              onChange={(date: Date) => {
                setCheckOutFromDate(date);
                setCheckOutToDate(undefined);
              }}
              selectsStart
              startDate={checkOutFromDate}
              endDate={checkOutToDate}
            />
            <ReactDatePicker
              selected={checkOutToDate}
              onChange={(date: Date) => {
                setCheckOutToDate(date);
              }}
              selectsEnd
              startDate={checkOutFromDate}
              endDate={checkOutToDate}
              minDate={checkOutFromDate}
            />
          </div>
        </div>
        <div className={styles['user-management-container__user-item__button-container']}>
          <Button
            onClick={handleUserSearchClick}
            className={styles['user-management-container__user-item__button-container__btn']}
            tracking={{
              action: 'close',
              category: 'unit-types',
              label: 'add-unit-type-modal'
            }}
            size={isMobileView ? ButtonSize.full : ButtonSize.medium}
          >
            search
          </Button>
          <Button
            onClick={handleClearClick}
            className={styles['user-management-container__user-item__button-container__btn']}
            tracking={{
              action: 'clear',
              category: 'unit-types',
              label: 'add-unit-type-modal'
            }}
            size={isMobileView ? ButtonSize.full : ButtonSize.medium}
            type={ButtonType.gray}
          >
            clear
          </Button>
          <Button
            onClick={handleUserExportClick}
            className={styles['user-management-container__user-item__button-container__btn']}
            size={isMobileView ? ButtonSize.full : ButtonSize.medium}
            type={ButtonType.success}
          >
            export
          </Button>
        </div>
      </div>
      {loadingData ? (
        <div className={styles['user-management-container__loader']}>
          <img src={`${PUBLIC_PATH}/assets/svgs/loader.svg`} alt="loader" />
        </div>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table aria-label="table">
              <TableHead>
                <TableRow>
                  <TableCell>AreaName</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>PrimaryGuestName</TableCell>
                  <TableCell>PrimaryGuestPhone</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>CheckInDate</TableCell>
                  <TableCell>CheckOutDate</TableCell>
                  <TableCell>CreatedAt</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users?.map((user) => (
                  <Row key={user.id} user={user} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {users.length === 0 && (
            <div className={styles['user-management-container__noData']}>
              <img src={`${PUBLIC_PATH}/assets/jpeg/no-records.jpg`} alt="no-data" /> No Data
              Available
            </div>
          )}
        </>
      )}
      {listCount > 0 && (
        <Pagination
          itemCounts={listCount}
          itemsPerPage={limit}
          offset={offset}
          handlePageChange={(val: number) => {
            getNewUser(val);
          }}
          handleItemsPerPage={(itemsPerPage: number) => {
            setLimit(itemsPerPage);
            getUsers({ ...searchQuery }, offset, itemsPerPage);
          }}
        />
      )}
    </div>
  );
};

export default connect()(UserManagementListContainer);
